import React, { useEffect, useRef, useState } from 'react';
import { userInitials } from './devUser';
import { AuthProvider, useAuth } from './auth/AuthContext';
import AuthScreen from './auth/AuthScreen';
import ProfileDialog from './auth/ProfileDialog';
import ProjectsHome from './screens/ProjectsHome';
import NewProject from './screens/NewProject';
import PropertyIdentification from './screens/PropertyIdentification';
import StatutoryAnalysis from './screens/StatutoryAnalysis';
import ExistingCondition from './screens/ExistingCondition';
import Stakeholders from './screens/Stakeholders';
import './RightsApp.css';

function Avatar({ user }) {
  return (
    <span className="rp-avatar">
      {user.image ? <img src={user.image} alt="" /> : userInitials(user.name)}
    </span>
  );
}

function UserMenu({ onEditProfile }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="rp-navitem-wrap" ref={ref}>
      <button type="button" className="rp-user" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open}>
        <Avatar user={user} />
        <span className="rp-user-name">{user.name}</span>
      </button>
      {open && (
        <div className="rp-navpop rp-usermenu" role="menu">
          <div className="rp-usermenu-id">
            <div className="rp-usermenu-name">{user.name}</div>
            <div className="rp-usermenu-email">{user.email}</div>
          </div>
          <div className="rp-navpop-sep" />
          <button type="button" role="menuitem" className="rp-navpop-item" onClick={() => { setOpen(false); onEditProfile?.(); }}>
            <i className="fi fi-rr-user" />
            <span>הפרופיל שלי</span>
          </button>
          <button type="button" role="menuitem" className="rp-navpop-item" onClick={() => { setOpen(false); logout(); }}>
            <i className="fi fi-rr-sign-out-alt" />
            <span>התנתקות</span>
          </button>
        </div>
      )}
    </div>
  );
}

// Auth gate: load the session, then show the login screen or the workspace.
export default function RightsApp() {
  return (
    <AuthProvider>
      <RightsGate />
    </AuthProvider>
  );
}

function RightsGate() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="rights-app rp-authloading" dir="rtl">
        <span className="rp-spinner" />
      </div>
    );
  }
  if (!user) return <AuthScreen />;
  return <RightsWorkspace />;
}

function RightsWorkspace() {
  // Internal screen routing for the workspace (no react-router in the app).
  const [screen, setScreen] = useState('home');
  const [profileOpen, setProfileOpen] = useState(false);
  // The full project loaded for editing (null = the form is a new project).
  const [formProject, setFormProject] = useState(null);
  // The project being identified on the next screen ({ id, name }).
  const [activeProject, setActiveProject] = useState(null);
  // Gallery scope: 'all' | 'mine' | 'shared'.
  const [projectFilter, setProjectFilter] = useState('all');

  const goNewProject = () => { setFormProject(null); setScreen('new-project'); };
  const goEditProject = (loaded) => { setFormProject(loaded); setScreen('new-project'); };
  const goHome = () => { setFormProject(null); setScreen('home'); };
  const goIdentify = (project) => { setActiveProject(project); setScreen('identify'); };
  const goStatutory = (project) => { setActiveProject(project); setScreen('statutory'); };
  const goExisting = (project) => { setActiveProject(project); setScreen('existing'); };

  // Single roadmap-navigation entry point. Screens pass the stage index plus
  // whatever they hold — `loaded` is the full getProject payload the edit form
  // needs, `project` the {id,name} the workflow screens take. Keeping the
  // index→screen map here means adding a stage touches one place.
  const goStage = (index, { loaded, project } = {}) => {
    if (index === 0) { if (loaded) goEditProject(loaded); return; }
    const target = project || activeProject;
    if (!target) return;
    if (index === 1) goIdentify(target);
    else if (index === 2) goStatutory(target);
    else if (index === 3) goExisting(target);
  };

  return (
    <div className="rights-app" dir="rtl">
      <header className="rp-topbar">
        <div className="rp-topbar-inner">
          <div className="rp-bar-start">
            <UserMenu onEditProfile={() => setProfileOpen(true)} />
            <nav className="rp-nav">
              {/* Plain link to the projects list — filtering lives on the home
                  screen's tabs, the single filter surface (spec 7.1). */}
              <button
                type="button"
                className={`rp-navitem ${screen === 'home' ? 'is-active' : ''}`}
                onClick={() => { setProjectFilter('all'); goHome(); }}
              >
                פרויקטים
              </button>
              <button
                type="button"
                className={`rp-navitem ${screen === 'stakeholders' ? 'is-active' : ''}`}
                onClick={() => setScreen('stakeholders')}
              >
                בעלי עניין
              </button>
            </nav>
          </div>

          <button type="button" className="rp-logo" onClick={goHome} aria-label="UrbaniX.ai — עמוד הבית">
            <span className="rp-logo-word">UrbaniX</span><span className="rp-logo-ai">.ai</span>
          </button>
        </div>
      </header>

      <main className="rp-main">
        {screen === 'home' && (
          <ProjectsHome
            filter={projectFilter}
            onFilterChange={setProjectFilter}
            onNewProject={goNewProject}
            onEditProject={goEditProject}
          />
        )}
        {screen === 'new-project' && <NewProject project={formProject} onCancel={goHome} onSaved={goHome} onNext={goIdentify} onGoStage={goStage} />}
        {screen === 'identify' && <PropertyIdentification project={activeProject} onBack={goHome} onNext={goStatutory} onPrev={goEditProject} onGoStage={goStage} />}
        {screen === 'statutory' && <StatutoryAnalysis project={activeProject} onBack={goHome} onNext={goExisting} onPrev={goIdentify} onGoStage={goStage} />}
        {screen === 'existing' && <ExistingCondition project={activeProject} onBack={goHome} onNext={goHome} onPrev={goStatutory} onGoStage={goStage} />}
        {screen === 'stakeholders' && <Stakeholders />}
      </main>

      {/* The document archive is a rail card on EVERY step (new-project,
          identify, statutory) — one consistent placement, no floating FAB. */}

      {profileOpen && <ProfileDialog onClose={() => setProfileOpen(false)} />}
    </div>
  );
}
